import * as React from "react";
import { Button } from "@/kit/ui/button";
import { cn } from "@/utils/tailwind";
import { usePathContext } from "./path";
import {
  Breadcrumb,
  BreadcrumbSeparator,
  BreadcrumbLink,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbList,
} from "@/kit/ui/breadcrumb";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "@/kit/ui/dropdown-menu";
import { FormattedMessage } from "react-intl";

interface PathBreadcrumbItemProps extends React.PropsWithChildren {
  active?: boolean;
  path: string[];
}

const PathBreadcrumbItem = ({
  path,
  active = false,
  children,
}: PathBreadcrumbItemProps) => {
  const { setPath } = usePathContext();
  return (
    <BreadcrumbLink asChild>
      <Button
        size="sm"
        variant="tertiary"
        hue="grey"
        onClick={() => setPath(path)}
        className={cn(!active && "text-grey-600 font-medium")}
      >
        {children}
      </Button>
    </BreadcrumbLink>
  );
};

const PathBreadcrumbEllipses = ({ path }: { path: string[] }) => {
  const { setPath } = usePathContext();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="cursor-pointer">
        <BreadcrumbEllipsis />
        <span className="sr-only">
          <FormattedMessage defaultMessage="Trigger schema path menu" />
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {path.map((item, index) => (
          <DropdownMenuItem
            key={index}
            onClick={() => setPath(path.slice(0, index + 1))}
          >
            {item}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const PathBreadcrumb = () => {
  const { path } = usePathContext();
  if (path.length === 0) {
    return <div />;
  }
  const last = path.slice(-1)[0];
  const rest = path.slice(0, -1);
  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <PathBreadcrumbItem path={[]}>Root</PathBreadcrumbItem>
        </BreadcrumbItem>
        {rest.length > 0 && (
          <>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              {rest.length > 1 ? (
                <PathBreadcrumbEllipses path={rest} />
              ) : (
                <PathBreadcrumbItem path={rest}>{rest[0]}</PathBreadcrumbItem>
              )}
            </BreadcrumbItem>
          </>
        )}
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <PathBreadcrumbItem path={path} active>
            {last}
          </PathBreadcrumbItem>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );
};
